import { useId, useMemo, useRef, useState } from 'react';
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';

import { storage } from '@/auth/firebase';

import styles from './FileUpload.module.css';

export interface UploadResult {
  downloadUrl: string;
  fileName: string;
  storagePath: string;
}

interface FileUploadProps {
  accept?: string;
  buttonLabel?: string;
  disabled?: boolean;
  maxFiles?: number;
  multiple?: boolean;
  onUploadComplete: (result: UploadResult) => void;
  /**
   * Called once each file's upload has ended, after its result or its error,
   * even when this control has gone: an editor that holds back saving until
   * its uploads are in counts them by these two calls.
   */
  onUploadEnd?: () => void;
  onUploadError?: (error: Error) => void;
  /** Called as each file's upload starts. */
  onUploadStart?: () => void;
  storagePath: string;
}

interface UploadItem extends UploadResult {
  errorMessage?: string;
  id: string;
  isImage: boolean;
  progress: number;
  status: 'error' | 'success' | 'uploading';
}

function UploadIcon() {
  return (
    <svg
      aria-hidden="true"
      className={styles.icon}
      fill="none"
      height="20"
      viewBox="0 0 24 24"
      width="20"
    >
      <path
        d="M12 16V4m0 0 4 4m-4-4-4 4M5 18v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function sanitizeFileName(fileName: string) {
  return fileName.replace(/\s+/g, '_');
}

export function FileUpload({
  accept,
  buttonLabel = 'Upload File',
  disabled = false,
  maxFiles = 4,
  multiple = false,
  onUploadComplete,
  onUploadEnd,
  onUploadError,
  onUploadStart,
  storagePath,
}: FileUploadProps) {
  const inputId = useId();
  const descriptionId = useId();
  const statusId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);

  const canSelectMore = useMemo(() => {
    if (!multiple) {
      return true;
    }

    return uploads.filter((upload) => upload.status !== 'error').length < maxFiles;
  }, [maxFiles, multiple, uploads]);

  const handleSelectFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const selectableFiles = Array.from(fileList);
    const remainingSlots = multiple
      ? Math.max(
          maxFiles - uploads.filter((upload) => upload.status !== 'error').length,
          0,
        )
      : 1;
    const filesToUpload = selectableFiles.slice(0, remainingSlots);

    await Promise.all(
      filesToUpload.map(async (file) => {
        const uploadId = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`;
        const storageObjectPath = `${storagePath.replace(/\/+$/, '')}/kudu_${Date.now()}_${sanitizeFileName(file.name)}`;
        const storageRef = ref(storage, storageObjectPath);

        setUploads((currentUploads) => [
          ...currentUploads,
          {
            downloadUrl: '',
            fileName: file.name,
            id: uploadId,
            isImage: file.type.startsWith('image/'),
            progress: 0,
            status: 'uploading',
            storagePath: storageRef.fullPath,
          },
        ]);
        onUploadStart?.();

        await new Promise<void>((resolve) => {
          const uploadTask = uploadBytesResumable(storageRef, file);
          const markFailed = (errorMessage?: string) => {
            setUploads((currentUploads) =>
              currentUploads.map((upload) =>
                upload.id === uploadId
                  ? {
                      ...upload,
                      errorMessage,
                      progress: 0,
                      status: 'error',
                    }
                  : upload,
              ),
            );
          };

          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress =
                snapshot.totalBytes > 0
                  ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                  : 0;

              setUploads((currentUploads) =>
                currentUploads.map((upload) =>
                  upload.id === uploadId
                    ? {
                        ...upload,
                        progress,
                      }
                    : upload,
                ),
              );
            },
            (uploadError) => {
              const normalizedError =
                uploadError instanceof Error
                  ? uploadError
                  : new Error('Upload failed.');

              markFailed(normalizedError.message);
              onUploadError?.(normalizedError);
              onUploadEnd?.();
              resolve();
            },
            async () => {
              let downloadUrl: string;

              try {
                downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              } catch {
                // Stored, but with no address nothing can show it: the upload
                // has failed, and ends here rather than hang at 100%.
                markFailed();
                onUploadError?.(new Error('Upload failed.'));
                onUploadEnd?.();
                resolve();
                return;
              }

              const result: UploadResult = {
                downloadUrl,
                fileName: file.name,
                storagePath: storageRef.fullPath,
              };

              setUploads((currentUploads) =>
                currentUploads.map((upload) =>
                  upload.id === uploadId
                    ? {
                        ...upload,
                        ...result,
                        progress: 100,
                        status: 'success',
                      }
                    : upload,
                ),
              );

              onUploadComplete(result);
              onUploadEnd?.();
              resolve();
            },
          );
        });
      }),
    );

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.controls}>
        <input
          accept={accept}
          aria-describedby={descriptionId}
          aria-label={buttonLabel}
          className={styles.input}
          disabled={disabled || !canSelectMore}
          id={inputId}
          multiple={multiple}
          onChange={(event) => {
            void handleSelectFiles(event.target.files);
          }}
          ref={inputRef}
          type="file"
        />
        <button
          aria-controls={statusId}
          aria-describedby={descriptionId}
          className={styles.button}
          disabled={disabled || !canSelectMore}
          onClick={() => {
            inputRef.current?.click();
          }}
          type="button"
        >
          <UploadIcon />
          <span>{buttonLabel}</span>
        </button>
        <p className={styles.description} id={descriptionId}>
          {multiple
            ? `Upload up to ${maxFiles} files.`
            : 'Select a file from your device to upload it to Kudu storage.'}
        </p>
      </div>

      <div aria-atomic="true" aria-live="polite" className={styles.statusRegion} id={statusId}>
        {uploads.map((upload) => (
          <div className={styles.uploadCard} key={upload.id}>
            <div className={styles.uploadHeader}>
              <div className={styles.uploadMeta}>
                <p className={styles.fileName}>{upload.fileName}</p>
                <p className={styles.uploadState}>
                  {upload.status === 'uploading'
                    ? `Uploading… ${Math.round(upload.progress)}%`
                    : upload.status === 'success'
                      ? 'Upload complete'
                      : upload.errorMessage ?? 'Upload failed'}
                </p>
              </div>
              {upload.status === 'error' ? (
                <button
                  className={styles.retryButton}
                  onClick={() => {
                    inputRef.current?.click();
                  }}
                  type="button"
                >
                  Try again
                </button>
              ) : null}
            </div>

            {upload.status === 'uploading' ? (
              <div
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={Math.round(upload.progress)}
                className={styles.progressBar}
                role="progressbar"
              >
                <span
                  className={styles.progressFill}
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            ) : null}

            {upload.status === 'success' ? (
              upload.isImage ? (
                <img
                  alt={`${upload.fileName} preview`}
                  className={styles.previewImage}
                  src={upload.downloadUrl}
                />
              ) : (
                <a
                  className={styles.fileLink}
                  href={upload.downloadUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {upload.fileName}
                </a>
              )
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
